// @flow

import {
  SET_VBMR_INITIALIZED,
  TOGGLE_VBMR,
  ACTIVATE_VBMR,
  DEACTIVATE_VBMR,
  TOGGLE_VBMR_MODAL,
  OPEN_VBMR_MODAL,
  CLOSE_VBMR_MODAL,
  VBMR_MODAL_TYPE_ACTIVATE,
  VBMR_MODAL_TYPE_DEACTIVATE,
  SET_QUEUED_ACTIVES_DISPATCHERS,
  CLEAR_QUEUED_ACTIVES_DISPATCHERS,
  SET_VBMR_SECTION,
  INCREASE_ACTIVATE_MODAL_TIMES_TRIGGERED,
  INCREASE_DEACTIVATE_MODAL_TIMES_TRIGGERED,
  RESET_MODALS_TIMES_TRIGGERED,
} from './constants';
import {
  defaultVbmrExtraId,
  vbmrCheckSections,
} from './config';
import {
  getReducer,
  getModalType,
  getSection,
  getAllowedTimesOfDisplayingModal,
  getTimesOfDisplayedModal,
} from './selectors';
import {
  selectDurationByPdbId,
  selectMileageByPdbId,
  selectClaimFreeByPdbId,
  selectColorByPdbId,
  selectPackageByPdbId,
  toggleAccessoryByPdbId,
  toggleExtraContractByPdbId,
  toggleBundleByPdbId,
  setActiveBundleExtras,
  updatePackageExtras,
} from '../actives/actions';
import { getActivePdbId } from '../../pdb/selectors/main';
import {
  getPackageFromActives,
  getActiveColorId,
  getExtraContractSelectedById,
} from '../actives/selectors';
import {
  getDefaultColor,
  getCheapestVbmrPackage,
  getCheapestVbmrColorId,
  getPackageContainsVbmrCars,
} from '../../pdb/selectors';
import { getVbmrAvailability } from '../../vbmr/selectors';
import { updateCart } from '../cart/actions';
import type {
  ToggleVbmr,
  ToggleVbmrModal,
  VbmrModalType,
} from './types';

export const setVbmrSection = (section: string) => ({
  type: SET_VBMR_SECTION,
  section,
});

export const setVbmrInitialized = () => ({
  type: SET_VBMR_INITIALIZED,
});

export const toggleVbmr = (toggleState: ToggleVbmr) => ({
  type: TOGGLE_VBMR,
  toggleState,
});

export const increaseActivateModalTimesTriggered = () => ({
  type: INCREASE_ACTIVATE_MODAL_TIMES_TRIGGERED,
});

export const increaseDeactivateModalTimesTriggered = () => ({
  type: INCREASE_DEACTIVATE_MODAL_TIMES_TRIGGERED,
});

export const resetModalsTimesTriggered = () => ({
  type: RESET_MODALS_TIMES_TRIGGERED,
});

export const deactivateAndSetInitialized = (): Function => (dispatch: Function) => {
  dispatch(toggleVbmr(DEACTIVATE_VBMR));
  dispatch(setVbmrInitialized());
};

export const initVbmr = (): Function => (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState();
  if (!getVbmrAvailability(state)) {
    dispatch(deactivateAndSetInitialized());
    return;
  }
  const activePdbId = getActivePdbId(state);
  if (!activePdbId) {
    dispatch(deactivateAndSetInitialized());
    return;
  }

  const cheapestPackageWithVbmr = getCheapestVbmrPackage(state, activePdbId || 0);
  const cheapestColorIdWithVbmr = getCheapestVbmrColorId(state, activePdbId);

  if (!cheapestPackageWithVbmr || !cheapestColorIdWithVbmr) {
    dispatch(deactivateAndSetInitialized());
    return;
  }
  dispatch(selectPackageByPdbId(activePdbId, cheapestPackageWithVbmr.id || 0));
  dispatch(selectColorByPdbId(activePdbId, cheapestColorIdWithVbmr));

  // We activate VBMR because at this point we are sure it should be applied.
  dispatch(toggleVbmr(ACTIVATE_VBMR));

  // Select the default VBMR extra, if it's not selected yet.
  if (!getExtraContractSelectedById(state, defaultVbmrExtraId)) {
    dispatch(toggleExtraContractByPdbId(activePdbId, defaultVbmrExtraId, 'selected'));
  }
  dispatch(resetModalsTimesTriggered());
  dispatch(setVbmrInitialized());
};

export const toggleVbmrModal = (
  toggleState: ToggleVbmrModal,
  modalType?: VbmrModalType,
) => ({
  type: TOGGLE_VBMR_MODAL,
  toggleState,
  modalType,
});

export const setQueuedActivesDispatchers = (
  queuedActivesDispatchers: {[index: string]: any[]},
) => ({
  type: SET_QUEUED_ACTIVES_DISPATCHERS,
  queuedActivesDispatchers,
});

export const clearQueuedActivesDispatchers = () => ({
  type: CLEAR_QUEUED_ACTIVES_DISPATCHERS,
});

export const dispatchQueuedActivesDispatchers = (): Function => (
  (dispatch: Function, getState: Function) => {
    const state = getReducer(getState());
    const queuedActivesDispatchers = state.queuedActivesDispatchers || {};
    if (queuedActivesDispatchers) {
      const activesActions: {[index: string]: Function} = {
        updateCart,
        selectDurationByPdbId,
        selectMileageByPdbId,
        selectClaimFreeByPdbId,
        selectColorByPdbId,
        selectPackageByPdbId,
        toggleAccessoryByPdbId,
        toggleExtraContractByPdbId,
        toggleBundleByPdbId,
        setActiveBundleExtras,
        updatePackageExtras,
      };
      Object.keys(queuedActivesDispatchers).forEach(action => {
        const parameters = queuedActivesDispatchers[action];
        if (activesActions[action]) {
          dispatch(activesActions[action].apply(action, parameters));
        }
      });
      dispatch(clearQueuedActivesDispatchers());
    }
  }
);

export const toggleDefaultExtraContract = (modalType: VbmrModalType): Function => (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState();
  const activePdbId = getActivePdbId(state);
  if (!activePdbId) {
    return;
  }
  const defaultVbmrExtraIsSelected = getExtraContractSelectedById(state, defaultVbmrExtraId);
  const displayModal: boolean = modalType === VBMR_MODAL_TYPE_ACTIVATE
    ? !defaultVbmrExtraIsSelected : defaultVbmrExtraIsSelected;
  if (displayModal) {
    dispatch(toggleExtraContractByPdbId(activePdbId, defaultVbmrExtraId, 'selected'));
  }
};

export const checkDisplayVbmrModal = (modalType: VbmrModalType): Function => (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState();
  const allowedTimesOfDisplayingModal: number = getAllowedTimesOfDisplayingModal(state, modalType);
  if (allowedTimesOfDisplayingModal === -1
    || getTimesOfDisplayedModal(state, modalType) < allowedTimesOfDisplayingModal) {
    dispatch(toggleVbmrModal(OPEN_VBMR_MODAL, modalType));
    dispatch(modalType === VBMR_MODAL_TYPE_ACTIVATE
      ? increaseActivateModalTimesTriggered()
      : increaseDeactivateModalTimesTriggered());
  } else {
    dispatch(toggleVbmr(modalType === VBMR_MODAL_TYPE_ACTIVATE
      ? ACTIVATE_VBMR : DEACTIVATE_VBMR));
    dispatch(dispatchQueuedActivesDispatchers());
    dispatch(toggleDefaultExtraContract(modalType));
  }
};

export const declineVbmr = (): Function => (dispatch: Function) => {
  dispatch(clearQueuedActivesDispatchers());
  dispatch(toggleVbmrModal(CLOSE_VBMR_MODAL));
};

export const acceptVbmr = (): Function => (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState();
  const modalType = getModalType(state);
  dispatch(dispatchQueuedActivesDispatchers());
  if (modalType === VBMR_MODAL_TYPE_DEACTIVATE) {
    dispatch(toggleVbmr(DEACTIVATE_VBMR));
  } else if (modalType === VBMR_MODAL_TYPE_ACTIVATE) {
    dispatch(toggleVbmr(ACTIVATE_VBMR));
  }
  dispatch(toggleVbmrModal(CLOSE_VBMR_MODAL));
  if (getSection(state) !== 'extra contract' && modalType) {
    dispatch(toggleDefaultExtraContract(modalType));
  }
};

// Global checks that need to be performed at every configuration change that could affect VBMR.
export const checkVbmr = (
  section: string,
  selectedConfiguration: any,
  queuedActivesDispatchers: {[index: string]: any[]},
): Function => (dispatch: Function, getState: Function) => {
  const state = getState();
  const vbmrConfigState = getReducer(state);

  // If VBMR is not initialized yet (API call checkVBMR is still pending), do nothing.
  if (!vbmrConfigState.isInitialized) {
    // Potential hook for displaying a loading indicator.
    return;
  }

  dispatch(setQueuedActivesDispatchers(queuedActivesDispatchers));

  // If VBMR is not available, or this section doesn't need a VBMR check,
  // we just dispatch the actives dispatchers.
  if (!getVbmrAvailability(state) || !vbmrCheckSections.includes(section)) {
    dispatch(dispatchQueuedActivesDispatchers());
    return;
  }

  dispatch(setVbmrSection(section));

  const activePdbId = getActivePdbId(state);
  let currentSelectionTriggersVbmr = true;

  // If an extra or accessory needs mounting first, it can never be an VBMR car.
  if ((section === 'extra contract' || section === 'accessory')
      && selectedConfiguration.needs_mounting) {
    currentSelectionTriggersVbmr = false;
  }

  // Check if the default - or active color in the currently selected package has VBMR.
  if (section === 'package') {
    const selectedPackageId = selectedConfiguration.id;
    const activePackageId = getPackageFromActives(state);
    if (selectedPackageId === activePackageId) {
      return;
    }
    const packageQueuedActivesDispatchers = getReducer(getState()).queuedActivesDispatchers || {};
    let colorIdToCheckForVbmr;
    if (packageQueuedActivesDispatchers.selectColorByPdbId) {
      const defaultColor = getDefaultColor(state, activePdbId, selectedPackageId);
      if (defaultColor) {
        colorIdToCheckForVbmr = defaultColor.id;
      }
    } else {
      const activeColorId = getActiveColorId(state);
      if (activeColorId) {
        colorIdToCheckForVbmr = activeColorId;
      }
    }
    if (colorIdToCheckForVbmr) {
      const colorHasVbmr = getPackageContainsVbmrCars(
        state,
        selectedPackageId,
        colorIdToCheckForVbmr,
        activePdbId,
      );
      if (!colorHasVbmr) {
        currentSelectionTriggersVbmr = false;
      }
    }
  }

  // Check if there is a VBMR car possible in the currently selected color.
  if (section === 'color') {
    const selectedColorId = selectedConfiguration.id;
    const activeColorId = getActiveColorId(state);
    if (selectedColorId === activeColorId) {
      return;
    }
    const activePackageId = getPackageFromActives(state);
    if (!activePackageId) {
      currentSelectionTriggersVbmr = false;
    } else {
      const vbmrCarInPackage = getPackageContainsVbmrCars(
        state,
        activePackageId,
        selectedColorId,
        activePdbId,
      );
      if (!vbmrCarInPackage) {
        currentSelectionTriggersVbmr = false;
      }
    }
  }

  if (currentSelectionTriggersVbmr) {
    if (vbmrConfigState.isActive) {
      if (getExtraContractSelectedById(state, defaultVbmrExtraId)
          && selectedConfiguration.id === defaultVbmrExtraId) {
        dispatch(checkDisplayVbmrModal(VBMR_MODAL_TYPE_DEACTIVATE));
        return;
      }
      dispatch(dispatchQueuedActivesDispatchers());
    } else if (['package', 'color'].includes(section)) {
      // If VBMR is not currently activated and the user clicks on a package or a color.
      dispatch(checkDisplayVbmrModal(VBMR_MODAL_TYPE_ACTIVATE));
    } else {
      dispatch(dispatchQueuedActivesDispatchers());
    }
  } else if (vbmrConfigState.isActive) {
    // If the selected configuration does not trigger VBMR, and VBMR is currently activated.
    dispatch(checkDisplayVbmrModal(VBMR_MODAL_TYPE_DEACTIVATE));
  } else {
    // If none of the VBMR modals apply, we just dispatch the actives dispatchers.
    dispatch(dispatchQueuedActivesDispatchers());
  }
};
